import { IChangePasswordDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import type { RuleObject } from "antd/es/form";
import * as React from "react";
import { UserService } from "../../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  IPasswordRequirement,
  isPasswordAcceptable,
  PASSWORD_REQUIREMENTS
} from "../../../shared/utility/password-rules";
import { SaveStatus } from "../../../shared/utility/save-status";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";

declare let window: any;

/** The `id` the dialog's footer submits — the buttons are siblings of the form, not children. */
const CHANGE_PASSWORD_FORM_ID: string = "change-password-form";

/** The commit changes a password; it does not save a document. */
const CHANGE_PASSWORD_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Changing", success: "Changed" };

/** The three fields, in the order they are filled. */
type TPasswordField = "currentPassword" | "newPassword" | "newPasswordConfirm";

export interface IChangePasswordState {
  changePasswordErrorMessage: string;
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** What is in the new-password box, so the requirements can answer as it is typed. */
  newPassword: string;
}

export interface IChangePwdProps {
  closeModal: any;
  showModal: boolean;
}

class ChangePwdModal extends React.PureComponent<IChangePwdProps & IWithHistoryProp, IChangePasswordState> {
  /**
   * The commit's state machine. A changed password is not visible anywhere once the dialog is
   * gone, so the toast stays; the button still says where the change stands, and holds its ✓
   * before the dialog closes.
   */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props) {
    super(props);
    this.state = {
      changePasswordErrorMessage: "",
      saveStatus: "idle",
      newPassword: ""
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  onSubmit = async (data: IChangePasswordDto) => {
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    try {
      UserService.ChangePassword(data).then(
        (res) => {
          if (res && res.status === 200) {
            this.setState({ changePasswordErrorMessage: "" });
            // The session is re-issued under the new password before the change is reported.
            UserService.Login({ username: window.userInfo.username, password: data.newPassword }).then(
              () => {
                KMainFunctions.displaySuccessNotification("Your password is changed.");
                this.saveStatus.succeed((): void => this.props.closeModal());
              },
              (error: unknown) => {
                this.saveStatus.fail();
                KMainFunctions.exceptionHandler(error);
              }
            );
          } else {
            this.saveStatus.fail();
          }
        },
        ({
          response: {
            data: { message }
          }
        }) => {
          if (message) {
            // The server's refusal is written under the fields, where it is read.
            this.saveStatus.fail();
            this.setState({
              changePasswordErrorMessage: message
            });
          } else {
            this.saveStatus.reset();
            this.props.navigator.goToPasswordChangeSuccess();
          }
        }
      );
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      this.saveStatus.fail();
    } finally {
    }
  };

  /** The new password twice: the server would refuse a mismatch, but not before it is sent. */
  matchesNewPassword = (_rule: RuleObject, value: string): Promise<void> => {
    const newPassword: string = this.formRef.current?.getFieldValue("newPassword") ?? "";
    if (!value || value === newPassword) return Promise.resolve();
    return Promise.reject(new Error("The two passwords do not match."));
  };

  formRef = React.createRef<any>();

  renderField = (
    field: TPasswordField,
    label: string,
    rules: RuleObject[],
    children?: React.ReactNode
  ): JSX.Element => (
    <div className="kuika-change-pwd__field">
      <span className="kuika-change-pwd__label">{label}</span>
      <Form.Item name={field} rules={rules}>
        <KuikaInput
          type="password"
          revealable
          placeholder={label}
          data-testid={`${field}Input`}
          autoComplete={field === "currentPassword" ? "current-password" : "new-password"}
          onChange={
            field === "newPassword"
              ? (_event, value): void => this.setState({ newPassword: String(value ?? "") })
              : undefined
          }
        />
      </Form.Item>
      {children}
    </div>
  );

  /**
   * All five requirements, answering as the password is typed. The dialog used to state
   * none of them and let the server say no — so the first a person heard of the rules was
   * a refusal, one rule at a time, after the request had gone.
   */
  renderRequirements = (): JSX.Element => {
    const password: string = this.state.newPassword;
    return (
      <ul className="kuika-change-pwd__requirements" data-testid="changePasswordRequirements">
        {PASSWORD_REQUIREMENTS.map((requirement: IPasswordRequirement) => {
          const isMet: boolean = requirement.isMet(password);
          return (
            <li
              key={requirement.id}
              data-testid={`passwordRequirement-${requirement.id}`}
              data-met={isMet ? "true" : "false"}
              className={`kuika-change-pwd__requirement${isMet ? " kuika-change-pwd__requirement--met" : ""}`}
            >
              {/* The mark carries the state, so it is not the colour alone that says "done". */}
              <span className="kuika-change-pwd__requirement-mark" aria-hidden="true">
                {isMet ? <KuikaSvg name="check" width={12} height={12} /> : null}
              </span>
              {requirement.label}
            </li>
          );
        })}
      </ul>
    );
  };

  render() {
    return (
      <KModal
        className="kuika-change-pwd-modal"
        header={<span data-testid="changePasswordModalTitle">Change password</span>}
        closeTestId="closeChangePasswordButton"
        showModal={this.props.showModal}
        closeModal={() => this.props.closeModal(false)}
        width={480}
        dontRenderSpinner
        footer={
          <KModalFooter
            onCancel={() => this.props.closeModal(false)}
            cancelTestId="changePasswordCancelButton"
            submitLabel="Change password"
            submitTestId="changePasswordSubmitButton"
            submitForm={CHANGE_PASSWORD_FORM_ID}
            // The requirement list is the feedback; this is what it is feedback for.
            disabled={!isPasswordAcceptable(this.state.newPassword)}
            status={this.state.saveStatus}
            statusLabels={CHANGE_PASSWORD_STATUS_LABELS}
          />
        }
      >
        <Form
          id={CHANGE_PASSWORD_FORM_ID}
          ref={this.formRef}
          className="kuika-change-pwd"
          name="basic"
          onFinish={this.onSubmit}
        >
          <div className="kuika-change-pwd__fields">
            {this.renderField("currentPassword", "Current password", [
              { required: true, message: "Please enter your current password." }
            ])}
            {this.renderField(
              "newPassword",
              "New password",
              // No rule messages under the field: the requirement list below says all five
              // at once, and the commit stays disabled until they are met. A validator here
              // would print the same refusal a second time, one rule at a time.
              [{ required: true, message: "Please enter a new password." }],
              this.renderRequirements()
            )}
            {this.renderField("newPasswordConfirm", "New password (confirm)", [
              { required: true, message: "Please repeat the new password." },
              { validator: this.matchesNewPassword }
            ])}
          </div>

          {/* What the server said. It runs a policy this dialog cannot see — password
              history, the account's own name, whatever the workspace has set — so it can
              still refuse a password that satisfies every requirement above. */}
          {this.state.changePasswordErrorMessage !== "" && (
            <div className="kuika-change-pwd__error" role="alert" data-testid="changePasswordError">
              <KuikaSvg name="info-circle" width={14} height={14} />
              <span>{this.state.changePasswordErrorMessage}</span>
            </div>
          )}
        </Form>
      </KModal>
    );
  }
}

const changePwdModal = withHistory<IChangePwdProps>(ChangePwdModal);
export { changePwdModal as ChangePwdModal };
